import React, { useState, useEffect } from "react";
import {
  ProjectWithStatistics,
  ClaimableProject,
  fetchClaimableProjects,
  claimProject,
  fetchIncorrectLabels,
  IncorrectLabel,
  fetchUserStats,
  UserStats,
  fetchUserDisputes,
  fetchAllPendingDisputes,
  fetchResolvedDisputes,
  fetchConflictingDisputes,
  ConflictingDisputeRun,
  getAcknowledgedMistakeIds,
  fetchProblemRunDisputes,
  deleteDispute,
  deleteMistakeAcknowledgment,
  getUserAcknowledgments,
  invalidateAPICache,
} from "../api";
import { ScoreDispute } from "../types";
import { formatDueDateWithTimezone, isOverdue } from "../utils/dateFormatting";
import { InfoPopup } from "./InfoPopup";
import DisputeInfoModal from "./DisputeInfoModal";
import Skeleton from "./Skeleton";
import PlaygroundContent from "./PlaygroundContent";
import "./ProjectList.css";

interface ProjectListProps {
  projects: ProjectWithStatistics[];
  onSelectProject: (projectId: string) => void;
  onSelectMistake?: (mistake: IncorrectLabel) => void;
  onSelectDispute?: (dispute: ScoreDispute) => void;
  activeTab?: TabType;
  onTabChange?: (tab: TabType) => void;
  userPermissions?: string[];
  loading?: boolean;
  projectsLoaded?: boolean;
}

type TabType = "my-projects" | "new-projects" | "my-mistakes" | "my-disputes" | "disputes" | "resolved-disputes" | "conflicting-disputes" | "playground";

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  onSelectProject,
  onSelectMistake,
  onSelectDispute,
  activeTab: externalActiveTab,
  onTabChange,
  userPermissions = [],
  loading = false,
  projectsLoaded = false,
}) => {
  const [internalActiveTab, setInternalActiveTab] =
    useState<TabType>(() => {
      // If user only has resolve_disputes permissions, default to disputes tab
      const hasOnlyResolveDisputes = userPermissions.some(p => p.startsWith('resolve_disputes_')) &&
                                     !userPermissions.some(p => p.startsWith('label_'));
      return hasOnlyResolveDisputes ? "disputes" : "my-projects";
    });
  const activeTab = externalActiveTab || internalActiveTab;
  const [claimableProjects, setClaimableProjects] = useState<
    ClaimableProject[]
  >([]);
  const [loadingClaimable, setLoadingClaimable] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewedProjects, setViewedProjects] = useState<Set<string>>(new Set());
  const [incorrectLabels, setIncorrectLabels] = useState<IncorrectLabel[]>([]);
  const [incorrectLabelsTotal, setIncorrectLabelsTotal] = useState(0);
  const [incorrectLabelsPage, setIncorrectLabelsPage] = useState(0);
  const [loadingMistakes, setLoadingMistakes] = useState(false);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [showAccuracyBanner, setShowAccuracyBanner] = useState(false);
  const [showMistakesInfoPopup, setShowMistakesInfoPopup] = useState(false);
  const [disputes, setDisputes] = useState<ScoreDispute[]>([]);
  const [disputesTotal, setDisputesTotal] = useState(0);
  const [disputesPage, setDisputesPage] = useState(0);
  const [loadingDisputes, setLoadingDisputes] = useState(false);
  const [allDisputes, setAllDisputes] = useState<ScoreDispute[]>([]);
  const [allDisputesTotal, setAllDisputesTotal] = useState(0);
  const [allDisputesPage, setAllDisputesPage] = useState(0);
  const [loadingAllDisputes, setLoadingAllDisputes] = useState(false);
  const [showDisputeInfoModal, setShowDisputeInfoModal] = useState(false);
  const [resolvedDisputes, setResolvedDisputes] = useState<ScoreDispute[]>([]);
  const [resolvedDisputesTotal, setResolvedDisputesTotal] = useState(0);
  const [resolvedDisputesPage, setResolvedDisputesPage] = useState(0);
  const [loadingResolvedDisputes, setLoadingResolvedDisputes] = useState(false);
  const [conflictingDisputes, setConflictingDisputes] = useState<ConflictingDisputeRun[]>([]);
  const [conflictingDisputesTotal, setConflictingDisputesTotal] = useState(0);
  const [conflictingDisputesPage, setConflictingDisputesPage] = useState(0);
  const [loadingConflictingDisputes, setLoadingConflictingDisputes] = useState(false);

  // Sorting states for disputes
  const [disputesSortBy, setDisputesSortBy] = useState<string>('created_at');
  const [disputesSortOrder, setDisputesSortOrder] = useState<string>('asc');
  const [resolvedDisputesSortBy, setResolvedDisputesSortBy] = useState<string>('updated_at');
  const [resolvedDisputesSortOrder, setResolvedDisputesSortOrder] = useState<string>('desc');

  // Search state for resolved disputes
  const [resolvedDisputesSearchQuery, setResolvedDisputesSearchQuery] = useState<string>('');
  const [resolvedDisputesSearchInput, setResolvedDisputesSearchInput] = useState<string>('');
  
  // State for acknowledged mistakes
  const [acknowledgedMistakeIds, setAcknowledgedMistakeIds] = useState<string[]>([]);
  const [showAcknowledgedMistakes, setShowAcknowledgedMistakes] = useState(false);
  const [loadingAcknowledged, setLoadingAcknowledged] = useState(false);
  
  // State for disputed mistakes - maps problem_run_id to dispute info (status and proposed_score)
  const [disputeInfoByRunId, setDisputeInfoByRunId] = useState<Map<string, { status: string; proposedScore: string }>>(new Map());
  
  // State for delete functionality
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState<'dispute' | 'acknowledgment' | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [acknowledgmentIdMap, setAcknowledgmentIdMap] = useState<Map<string, string>>(new Map());
  
  // State for deleting disputes
  const [deletingDisputeId, setDeletingDisputeId] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [disputeToDelete, setDisputeToDelete] = useState<ScoreDispute | null>(null);
  const [hoveredDeleteButton, setHoveredDeleteButton] = useState<string | null>(null);
  
  // Calculate unacknowledged mistakes count
  const unacknowledgedMistakesCount = incorrectLabels.filter(
    mistake => !acknowledgedMistakeIds.includes(mistake.human_label_id)
  ).length;
  
  // For the tab display, we need the total unacknowledged count from all pages
  const [totalUnacknowledgedCount, setTotalUnacknowledgedCount] = useState(0);

  // Check if user has any resolve_disputes permissions
  const hasResolveDisputesPermission = userPermissions.some(perm => 
    perm.startsWith('resolve_disputes_')
  );
  
  // Check if user has any label permissions
  const hasLabelPermission = userPermissions.some(perm => 
    perm.startsWith('label_')
  );
  
  // Check if user has permissions for software with playground support
  const hasPlaygroundAccess = userPermissions.some(perm => {
    // Extract software name from permission (e.g., 'label_immich' -> 'immich')
    const labelMatch = perm.match(/^(?:create_|label_)(.+)$/);
    if (labelMatch) {
      const software = labelMatch[1];
      // Only these software have playground support
      return ['immich', 'odoo', 'erpnext'].includes(software);
    }
    return false;
  });

  // Load viewed projects from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("viewedClaimableProjects");
    if (stored) {
      setViewedProjects(new Set(JSON.parse(stored)));
    }
  }, []);

  useEffect(() => {
    // Always load claimable projects on mount to show the count
    loadClaimableProjects();
    // Also load mistake count to show in tab
    loadMistakeCount();
    // Load user stats to check accuracy
    loadUserStats();
    // Load dispute count to show in tab
    loadDisputeCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResolveDisputesPermission]);

  useEffect(() => {
    // Load data based on active tab
    if (activeTab === "new-projects") {
      loadClaimableProjects();
    } else if (activeTab === "my-mistakes") {
      loadIncorrectLabelsData();
      // Check if this is the first time visiting My Mistakes tab
      const hasSeenMistakesInfo = localStorage.getItem("hasSeenMistakesInfo");
      if (!hasSeenMistakesInfo) {
        setShowMistakesInfoPopup(true);
        localStorage.setItem("hasSeenMistakesInfo", "true");
      }
    } else if (activeTab === "my-disputes") {
      loadDisputesData();
    } else if (activeTab === "disputes") {
      loadAllDisputesData();
    } else if (activeTab === "resolved-disputes") {
      loadResolvedDisputesData();
    } else if (activeTab === "conflicting-disputes") {
      loadConflictingDisputesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  // Reload disputes when sorting changes
  useEffect(() => {
    if (activeTab === "disputes") {
      loadAllDisputesData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disputesSortBy, disputesSortOrder]);
  
  // Reload resolved disputes when sorting changes
  useEffect(() => {
    if (activeTab === "resolved-disputes") {
      loadResolvedDisputesData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedDisputesSortBy, resolvedDisputesSortOrder]);

  // Debounce search input for resolved disputes
  useEffect(() => {
    const timer = setTimeout(() => {
      setResolvedDisputesSearchQuery(resolvedDisputesSearchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [resolvedDisputesSearchInput]);

  // Reload resolved disputes when search query changes
  useEffect(() => {
    if (activeTab === "resolved-disputes") {
      loadResolvedDisputesData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedDisputesSearchQuery]);

  const loadClaimableProjects = async () => {
    setLoadingClaimable(true);
    setError(null);
    try {
      const claimable = await fetchClaimableProjects();
      setClaimableProjects(claimable);

      // Mark all projects as viewed when user clicks on the tab
      if (activeTab === "new-projects" && claimable.length > 0) {
        const viewedArray = Array.from(viewedProjects);
        const newIds = claimable.map((p) => p.id);
        const allIds = new Set([...viewedArray, ...newIds]);
        setViewedProjects(allIds);
        localStorage.setItem(
          "viewedClaimableProjects",
          JSON.stringify(Array.from(allIds)),
        );
      }
    } catch (err: any) {
      console.error("Error loading claimable projects:", err);
      setError("Failed to load available projects");
    } finally {
      setLoadingClaimable(false);
    }
  };

  const loadMistakeCount = async () => {
    try {
      // Just fetch the first page with limit 1 to get the total count
      const response = await fetchIncorrectLabels(0, 1);
      setIncorrectLabelsTotal(response.metadata.total);
      
      // Also get acknowledged IDs to calculate unacknowledged count
      try {
        const acknowledgedIds = await getAcknowledgedMistakeIds();
        // We need to fetch all mistakes to get accurate count
        // This is not ideal but necessary for accurate counting
        const allMistakesResponse = await fetchIncorrectLabels(0, response.metadata.total);
        const unacknowledgedCount = allMistakesResponse.items.filter(
          mistake => !acknowledgedIds.includes(mistake.human_label_id)
        ).length;
        setTotalUnacknowledgedCount(unacknowledgedCount);
      } catch (err) {
        // If we can't get acknowledged IDs, use total count
        setTotalUnacknowledgedCount(response.metadata.total);
      }
    } catch (err: any) {
      console.error("Error loading mistake count:", err);
      // Don't show error for count loading
    }
  };

  const loadUserStats = async () => {
    try {
      const stats = await fetchUserStats();
      setUserStats(stats);

      // Show banner if recent accuracy is below 90% and user has at least 100 labels
      if (
        stats.recent_accuracy !== null && 
        stats.recent_accuracy < 0.9 && 
        stats.total_labels >= 100
      ) {
        setShowAccuracyBanner(true);
      }
    } catch (err: any) {
      console.error("Error loading user stats:", err);
      // Don't show error for stats loading
    }
  };

  const loadDisputeCount = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      // Just fetch the first page with limit 1 to get the total count
      const response = await fetchUserDisputes(userId, 0, 1);
      setDisputesTotal(response.total);
      
      // Also load all disputes count if user has permission
      if (hasResolveDisputesPermission) {
        const allResponse = await fetchAllPendingDisputes(0, 1);
        setAllDisputesTotal(allResponse.total);

        // Load resolved disputes count
        const resolvedResponse = await fetchResolvedDisputes(0, 1);
        setResolvedDisputesTotal(resolvedResponse.total);

        // Load conflicting disputes count
        const conflictingResponse = await fetchConflictingDisputes(0, 1);
        setConflictingDisputesTotal(conflictingResponse.total);
      }
    } catch (err: any) {
      console.error("Error loading dispute count:", err);
      // Don't show error for count loading
    }
  };

  const loadDisputesData = async (page: number = 0) => {
    setLoadingDisputes(true);
    setError(null);
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        throw new Error("User ID not found");
      }

      const response = await fetchUserDisputes(userId, page, 20);
      setDisputes(response.disputes);
      setDisputesTotal(response.total);
      setDisputesPage(page);
    } catch (err: any) {
      console.error("Error loading disputes:", err);
      setError("Failed to load disputes");
    } finally {
      setLoadingDisputes(false);
    }
  };

  const loadAllDisputesData = async (page: number = 0) => {
    // Don't try to load if user doesn't have permission
    if (!hasResolveDisputesPermission) {
      return;
    }
    
    setLoadingAllDisputes(true);
    setError(null);
    try {
      const response = await fetchAllPendingDisputes(page, 20, disputesSortBy, disputesSortOrder);
      setAllDisputes(response.disputes);
      setAllDisputesTotal(response.total);
      setAllDisputesPage(page);
    } catch (err: any) {
      console.error("Error loading all disputes:", err);
      setError("Failed to load disputes");
    } finally {
      setLoadingAllDisputes(false);
    }
  };

  const loadResolvedDisputesData = async (page: number = 0) => {
    // Don't try to load if user doesn't have permission
    if (!hasResolveDisputesPermission) {
      return;
    }

    setLoadingResolvedDisputes(true);
    setError(null);
    try {
      const response = await fetchResolvedDisputes(
        page,
        20,
        resolvedDisputesSortBy,
        resolvedDisputesSortOrder,
        resolvedDisputesSearchQuery
      );
      setResolvedDisputes(response.disputes);
      setResolvedDisputesTotal(response.total);
      setResolvedDisputesPage(page);
    } catch (err: any) {
      console.error("Error loading resolved disputes:", err);
      setError("Failed to load resolved disputes");
    } finally {
      setLoadingResolvedDisputes(false);
    }
  };

  const loadConflictingDisputesData = async (page: number = 0) => {
    // Don't try to load if user doesn't have permission
    if (!hasResolveDisputesPermission) {
      return;
    }

    setLoadingConflictingDisputes(true);
    setError(null);
    try {
      const response = await fetchConflictingDisputes(page, 20);
      setConflictingDisputes(response.conflicting_runs);
      setConflictingDisputesTotal(response.total);
      setConflictingDisputesPage(page);
    } catch (err: any) {
      console.error("Error loading conflicting disputes:", err);
      setError("Failed to load conflicting disputes");
    } finally {
      setLoadingConflictingDisputes(false);
    }
  };

  const isProjectNew = (projectId: string): boolean => {
    return !viewedProjects.has(projectId);
  };

  const getNewProjectsCount = (): number => {
    return claimableProjects.filter((p) => isProjectNew(p.id)).length;
  };

  const loadIncorrectLabelsData = async (page: number = 0) => {
    setLoadingMistakes(true);
    setError(null);
    try {
      const response = await fetchIncorrectLabels(page, 20);
      setIncorrectLabels(response.items);
      setIncorrectLabelsTotal(response.metadata.total);
      setIncorrectLabelsPage(page);
      
      // Also load acknowledged mistake IDs
      loadAcknowledgedMistakes();
      
      // Load disputes for all problem runs in parallel
      if (response.items.length > 0) {
        const userId = localStorage.getItem("userId");
        const disputePromises = response.items.map(mistake =>
          fetchProblemRunDisputes(mistake.problem_run_id)
            .then(disputes => {
              // Find the user's own dispute (any type, including bad_task)
              const userDispute = disputes.find(d => d.user_id === userId);
              return {
                runId: mistake.problem_run_id,
                status: userDispute?.status || null,
                proposedScore: userDispute?.proposed_score || null
              };
            })
            .catch(() => ({ runId: mistake.problem_run_id, status: null, proposedScore: null }))
        );

        const disputeResults = await Promise.all(disputePromises);
        const newDisputeInfoMap = new Map<string, { status: string; proposedScore: string }>();
        disputeResults.forEach(result => {
          if (result.status && result.proposedScore) {
            newDisputeInfoMap.set(result.runId, {
              status: result.status,
              proposedScore: result.proposedScore
            });
          }
        });
        setDisputeInfoByRunId(newDisputeInfoMap);
      }
    } catch (err: any) {
      console.error("Error loading incorrect labels:", err);
      setError("Failed to load mistakes");
    } finally {
      setLoadingMistakes(false);
    }
  };
  
  const loadAcknowledgedMistakes = async () => {
    setLoadingAcknowledged(true);
    try {
      const acknowledgedIds = await getAcknowledgedMistakeIds();
      setAcknowledgedMistakeIds(acknowledgedIds);
      
      // Also fetch full acknowledgment data to get IDs for deletion
      try {
        const acknowledgments = await getUserAcknowledgments();
        const idMap = new Map<string, string>();
        acknowledgments.forEach(ack => {
          idMap.set(ack.human_label_id, ack.id);
        });
        setAcknowledgmentIdMap(idMap);
      } catch (err) {
        console.error('Error fetching acknowledgments:', err);
      }
      
      // Update the total unacknowledged count
      if (incorrectLabelsTotal > 0) {
        const allMistakesResponse = await fetchIncorrectLabels(0, incorrectLabelsTotal);
        const unacknowledgedCount = allMistakesResponse.items.filter(
          mistake => !acknowledgedIds.includes(mistake.human_label_id)
        ).length;
        setTotalUnacknowledgedCount(unacknowledgedCount);
      }
    } catch (err: any) {
      console.error("Error loading acknowledged mistakes:", err);
    } finally {
      setLoadingAcknowledged(false);
    }
  };

  const handleClaimProject = async (projectQueueAssignmentId: string) => {
    setClaiming(true);
    setError(null);
    try {
      const result = await claimProject(projectQueueAssignmentId);
      // Navigate to the claimed project
      onSelectProject(result.project_id);
    } catch (err: any) {
      console.error("Error claiming project:", err);
      if (err.response?.status === 404 || err.response?.status === 409) {
        setError(
          "This project is no longer available. Someone else may have already claimed it.",
        );
      } else {
        setError(
          err.response?.data?.detail ||
            "Failed to claim project. Please try again.",
        );
      }
      // Refresh the list
      await loadClaimableProjects();
    } finally {
      setClaiming(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  const handleDeleteDispute = async () => {
    if (!disputeToDelete) return;
    
    setDeletingDisputeId(disputeToDelete.id);
    try {
      await deleteDispute(disputeToDelete.id);
      // Reload disputes to reflect the deletion
      await loadDisputesData(disputesPage);
      setShowDeleteConfirmModal(false);
      setDisputeToDelete(null);
    } catch (error: any) {
      console.error("Error deleting dispute:", error);
      alert("Failed to delete dispute. Please try again.");
    } finally {
      setDeletingDisputeId(null);
    }
  };

  const handleDeleteClick = (type: 'dispute' | 'acknowledgment', targetId: string) => {
    setDeleteType(type);
    setDeleteTargetId(targetId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteType || !deleteTargetId) return;

    setIsDeleting(true);
    try {
      if (deleteType === 'dispute') {
        // Find the dispute for the problem run
        let disputeForRun = disputes.find(d => d.problem_run_id === deleteTargetId) ||
                            allDisputes.find(d => d.problem_run_id === deleteTargetId);

        // If not found in the loaded disputes arrays, fetch it directly for this problem run
        if (!disputeForRun) {
          const problemRunDisputes = await fetchProblemRunDisputes(deleteTargetId);
          // Find the user's own dispute (pending or rejected)
          const userId = localStorage.getItem("userId");
          disputeForRun = problemRunDisputes.find(d =>
            d.user_id === userId &&
            (d.status === 'pending' || d.status === 'rejected')
          );
        }

        if (disputeForRun) {
          await deleteDispute(disputeForRun.id);
          // Reload disputes
          await loadDisputesData(disputesPage);
          // Update dispute info map
          const newDisputeInfoMap = new Map(disputeInfoByRunId);
          newDisputeInfoMap.delete(deleteTargetId);
          setDisputeInfoByRunId(newDisputeInfoMap);
          // Invalidate the cache for this problem run's disputes
          invalidateAPICache(`/problem-runs/${deleteTargetId}/score-disputes`);
        } else {
          alert('No deletable dispute found for this problem run.');
        }
      } else if (deleteType === 'acknowledgment') {
        const acknowledgmentId = acknowledgmentIdMap.get(deleteTargetId);
        if (acknowledgmentId) {
          await deleteMistakeAcknowledgment(acknowledgmentId);
          // Reload acknowledged mistakes
          await loadAcknowledgedMistakes();
          // Invalidate caches
          invalidateAPICache('/me/incorrect-labels');
          invalidateAPICache('/human-labels/mistakes/acknowledged-ids');
        }
      }
      setShowDeleteModal(false);
    } catch (error: any) {
      console.error(`Error deleting ${deleteType}:`, error);
      alert(`Failed to delete ${deleteType}. Please try again.`);
    } finally {
      setIsDeleting(false);
      setDeleteType(null);
      setDeleteTargetId(null);
    }
  };

  const getMatchingReasons = (mistake: IncorrectLabel) => {
    // Determine what the correct answer should be
    const correctAnswerIsSuccess = mistake.final_score === 1.0;
    const correctLabel = correctAnswerIsSuccess ? 'success' : 'failed';

    // Filter other users' labels that match the final score and have notes
    const matchingLabels = mistake.all_labels.filter(label =>
      label.label === correctLabel &&
      label.notes &&
      label.notes.trim() !== '' &&
      // Exclude the current user's label
      label.user_id !== mistake.all_labels.find(l => l.notes === mistake.user_label_notes)?.user_id
    );

    // Return up to 2 matching reasons
    return matchingLabels.slice(0, 2).map(label => ({
      username: label.username,
      notes: label.notes
    }));
  };

  return (
    <div className="project-list-container">
      <InfoPopup
        show={showMistakesInfoPopup}
        onClose={() => setShowMistakesInfoPopup(false)}
        title="Quick tip"
      >
        <p>
          <strong>Right-click any video</strong> to download the output file
          from the AI's solution.
        </p>
        <p>
          This helps you understand why a solution was correct or incorrect by
          examining the actual output.
        </p>
        <p>
          You can do this both here in My Mistakes and when labeling projects!
        </p>
      </InfoPopup>

      {showAccuracyBanner && (
        <div className="accuracy-warning-banner">
          <div className="banner-content">
            <span className="banner-icon">⚠️</span>
            <div className="banner-text">
              Your recent labeling accuracy has been low. If you do not improve
              your accuracy, you may be banned from labeling future projects.
              Please consult the{" "}
              <button
                className="banner-link"
                onClick={() => handleTabChange("my-mistakes")}
              >
                'My Mistakes'
              </button>{" "}
              tab to see some of your errors. If you believe the label on a
              problem is incorrect, please submit a dispute to the problem from
              the 'My Mistakes' view.
            </div>
          </div>
        </div>
      )}

      <div className="tabs-container">
        <div className="tabs">
          {hasLabelPermission && (
            <>
              <button
                className={`tab ${activeTab === "my-projects" ? "active" : ""}`}
                onClick={() => handleTabChange("my-projects")}
              >
                My Projects
              </button>
              <button
                className={`tab ${activeTab === "new-projects" ? "active" : ""}`}
                onClick={() => handleTabChange("new-projects")}
              >
                New Projects{" "}
                {claimableProjects.length > 0 && `(${claimableProjects.length})`}
                {getNewProjectsCount() > 0 && (
                  <span className="new-badge">New!</span>
                )}
              </button>
              <button
                className={`tab ${activeTab === "my-mistakes" ? "active" : ""}`}
                onClick={() => handleTabChange("my-mistakes")}
              >
                My Mistakes{" "}
                {totalUnacknowledgedCount > 0 && `(${totalUnacknowledgedCount})`}
              </button>
              <button
                className={`tab ${activeTab === "my-disputes" ? "active" : ""}`}
                onClick={() => handleTabChange("my-disputes")}
              >
                My Disputes {disputesTotal > 0 && `(${disputesTotal})`}
              </button>
            </>
          )}
          {hasResolveDisputesPermission && (
            <>
              <button
                className={`tab ${activeTab === "disputes" ? "active" : ""}`}
                onClick={() => handleTabChange("disputes")}
              >
                Disputes {allDisputesTotal > 0 && `(${allDisputesTotal})`}
              </button>
              <button
                className={`tab ${activeTab === "resolved-disputes" ? "active" : ""}`}
                onClick={() => handleTabChange("resolved-disputes")}
              >
                Resolved Disputes {resolvedDisputesTotal > 0 && `(${resolvedDisputesTotal})`}
              </button>
              <button
                className={`tab ${activeTab === "conflicting-disputes" ? "active" : ""}`}
                onClick={() => handleTabChange("conflicting-disputes")}
              >
                Conflicting Disputes {conflictingDisputesTotal > 0 && `(${conflictingDisputesTotal})`}
              </button>
            </>
          )}
          {hasPlaygroundAccess && (
            <button
              className={`tab ${activeTab === "playground" ? "active" : ""}`}
              onClick={() => handleTabChange("playground")}
            >
              Playground
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeTab === "my-projects" && hasLabelPermission ? (
        <>
          {loading ? (
            <div className="project-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="project-card skeleton-card">
                  <Skeleton height={24} className="skeleton-title" style={{ marginBottom: 8 }} />
                  <Skeleton height={16} width="60%" style={{ marginBottom: 16 }} />
                  <div style={{ marginBottom: 8 }}>
                    <Skeleton height={16} width="40%" style={{ marginBottom: 4 }} />
                    <Skeleton height={16} width="50%" />
                  </div>
                  <Skeleton height={20} width="30%" style={{ marginTop: 16 }} />
                </div>
              ))}
            </div>
          ) : (
            projects.length === 0 && projectsLoaded ? (
              <div className="no-projects">No projects assigned to you.</div>
            ) : (
              <div className="project-grid">
              {projects
                .sort(
                  (a, b) =>
                    new Date(b.created_at + "Z").getTime() -
                    new Date(a.created_at + "Z").getTime(),
                )
                .map((project) => {
                  const isCompleted =
                    project.remaining_problem_runs === 0 &&
                    project.total_problem_runs > 0;
                  const isPastDue =
                    project.due_date && isOverdue(project.due_date);
                  const isOverdueProject = isPastDue && !isCompleted;
                  return (
                    <a
                      key={project.id}
                      href={`/project/${project.id}`}
                      className={`project-card ${isCompleted ? "completed" : ""} ${isOverdueProject ? "overdue disabled" : ""} ${isPastDue && isCompleted ? "completed-past-due" : ""}`}
                      onClick={(e) => {
                        // Allow command+click (Mac) or ctrl+click (Windows) to open in new tab
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                          return;
                        }
                        e.preventDefault();
                        if (!isOverdueProject) {
                          onSelectProject(project.id);
                        }
                      }}
                      title={
                        isOverdueProject
                          ? "This project is past the due date"
                          : isPastDue && isCompleted
                            ? "This project was completed but is past the due date - labels cannot be modified"
                            : undefined
                      }
                      style={{ textDecoration: "none" }}
                    >
                      <h3>
                        <span>{project.name}</span>
                        <div className="badges">
                          {isPastDue && !isCompleted && (
                            <span className="overdue-badge">Past Due</span>
                          )}
                          {isCompleted && (
                            <span className="completion-badge">✓ Complete</span>
                          )}
                        </div>
                      </h3>
                      <div className="project-stats">
                        <div className="stat">
                          <span className="stat-label">Labeled:</span>
                          <span className="stat-value">
                            {project.labeled_problem_runs}
                          </span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Total:</span>
                          <span className="stat-value">
                            {project.total_problem_runs}
                          </span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Remaining:</span>
                          <span className="stat-value">
                            {project.remaining_problem_runs}
                          </span>
                        </div>
                      </div>
                      {project.status && (
                        <div className="project-status">
                          <span className="stat-label">Status:</span>
                          <span className="stat-value">{project.status}</span>
                        </div>
                      )}
                      {project.due_date && (
                        <div
                          className={`project-due-date ${isOverdueProject ? "overdue" : ""}`}
                        >
                          <span className="stat-label">Due:</span>
                          <span className="stat-value">
                            {formatDueDateWithTimezone(project.due_date)}
                          </span>
                        </div>
                      )}
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${
                              project.total_problem_runs > 0
                                ? (project.labeled_problem_runs /
                                    project.total_problem_runs) *
                                  100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </a>
                  );
                })}
            </div>
            )
          )}
        </>
      ) : activeTab === "new-projects" && hasLabelPermission ? (
        <>
          {loadingClaimable ? (
            <div className="project-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="project-card claimable skeleton-card">
                  <Skeleton height={24} className="skeleton-title" style={{ marginBottom: 8 }} />
                  <Skeleton height={16} width="70%" style={{ marginBottom: 16 }} />
                  <div style={{ marginBottom: 8 }}>
                    <Skeleton height={16} width="50%" style={{ marginBottom: 4 }} />
                    <Skeleton height={16} width="60%" />
                  </div>
                  <Skeleton height={36} style={{ marginTop: 16 }} />
                </div>
              ))}
            </div>
          ) : claimableProjects.length === 0 ? (
            <div className="no-projects">
              No new projects available at the moment. Check back later!
            </div>
          ) : (
            <div className="project-grid">
              {claimableProjects.map((project) => (
                <div key={project.id} className="project-card claimable">
                  <h3>
                    {project.name}
                    {isProjectNew(project.id) && (
                      <span className="new-indicator">New!</span>
                    )}
                  </h3>
                  <div className="project-stats">
                    <div className="stat">
                      <span className="stat-label">Runs to label:</span>
                      <span className="stat-value">{project.total_runs}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Available since:</span>
                      <span className="stat-value">
                        {new Date(
                          project.created_at + "Z",
                        ).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label" style={{ color: 'red' }}>Due by:</span>
                      <span className="stat-value" style={{ color: 'red' }}>
                        {formatDueDateWithTimezone(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString())}
                      </span>
                    </div>
                  </div>
                  <button
                    className="claim-button"
                    onClick={() => handleClaimProject(project.id)}
                    disabled={claiming}
                  >
                    {claiming ? "Claiming..." : "Claim Project"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : activeTab === "my-mistakes" && hasLabelPermission ? (
        <>
          <div className="mistakes-header">
            <label className="toggle-acknowledged">
              <input
                type="checkbox"
                checked={showAcknowledgedMistakes}
                onChange={(e) => setShowAcknowledgedMistakes(e.target.checked)}
              />
              Show acknowledged mistakes
            </label>
          </div>
          {loadingMistakes ? (
            <div className="project-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="project-card mistake-card skeleton-card">
                  <Skeleton height={24} className="skeleton-title" style={{ marginBottom: 8 }} />
                  <div style={{ marginBottom: 12 }}>
                    <Skeleton height={16} width="45%" style={{ marginBottom: 4 }} />
                    <Skeleton height={16} width="40%" />
                  </div>
                  <Skeleton height={16} width="60%" style={{ marginBottom: 8 }} />
                  <Skeleton height={14} width="50%" />
                </div>
              ))}
            </div>
          ) : incorrectLabels.length === 0 ? (
            <div className="no-projects">
              Great job! You have no incorrect labels.
            </div>
          ) : (
            <>
              {/* Check if we have any mistakes to show after filtering */}
              {incorrectLabels.filter((mistake) => {
                const isAcknowledged = acknowledgedMistakeIds.includes(mistake.human_label_id);
                return showAcknowledgedMistakes || !isAcknowledged;
              }).length === 0 ? (
                <div className="no-projects">
                  {showAcknowledgedMistakes 
                    ? "No mistakes to display." 
                    : "Great job! All your mistakes have been acknowledged."}
                </div>
              ) : (
                <div className="project-grid">
                  {incorrectLabels
                    .filter((mistake) => {
                      // Filter based on acknowledged status
                      const isAcknowledged = acknowledgedMistakeIds.includes(mistake.human_label_id);
                      return showAcknowledgedMistakes || !isAcknowledged;
                    })
                    .map((mistake) => {
                    const isAcknowledged = acknowledgedMistakeIds.includes(mistake.human_label_id);
                    return (
                      <a
                        key={mistake.problem_run_id}
                        href={`/run/${mistake.problem_run_id}?mode=mistake`}
                        className={`project-card mistake-card ${isAcknowledged ? "acknowledged" : ""}`}
                        onClick={(e) => {
                          // Allow command+click (Mac) or ctrl+click (Windows) to open in new tab
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                            return;
                          }
                          e.preventDefault();
                          if (onSelectMistake) {
                            onSelectMistake(mistake);
                          }
                        }}
                        style={{ textDecoration: "none" }}
                      >
                        <h3>{mistake.problem_name}</h3>
                        <div className="mistake-details">
                          <div className="label-comparison">
                            <div className="label-item">
                              <span className="label-type">Your Label:</span>
                              <span className={`label-value ${mistake.user_label}`}>
                                {mistake.user_label === "success"
                                  ? "Success"
                                  : mistake.user_label === "bad_task"
                                  ? "Bad Task"
                                  : "Failed"}
                              </span>
                            </div>
                            <div className="label-item">
                              <span className="label-type">Actual Result:</span>
                              <span
                                className={`label-value ${mistake.final_score === 1.0 ? "success" : "failed"}`}
                              >
                                {mistake.final_score === 1.0 ? "Success" : "Failed"}
                              </span>
                            </div>
                          </div>
                          <div className="mistake-date">
                            Labeled on{" "}
                            {new Date(mistake.labeled_at + "Z").toLocaleString()}
                          </div>

                          {/* Display user's reason if available */}
                          {mistake.user_label_notes && mistake.user_label_notes.trim() !== '' && (
                            <div className="reason-section">
                              <div className="reason-header">Your Reason:</div>
                              <div className="reason-text">{mistake.user_label_notes}</div>
                            </div>
                          )}

                          {/* Display rejection reason for rejected bad task disputes */}
                          {mistake.dispute_rejection_reason && mistake.user_label === 'bad_task' && (
                            <div className="reason-section rejection-reason">
                              <div className="reason-header">Rejection Reason:</div>
                              <div className="reason-text">{mistake.dispute_rejection_reason}</div>
                            </div>
                          )}

                          {/* Display other users' matching reasons */}
                          {(() => {
                            const matchingReasons = getMatchingReasons(mistake);
                            if (matchingReasons.length > 0) {
                              return (
                                <div className="reason-section">
                                  <div className="reason-header">
                                    Other users who marked it {mistake.final_score === 1.0 ? 'Success' : 'Failed'}:
                                  </div>
                                  {matchingReasons.map((reason, idx) => (
                                    <div key={idx} className="other-reason">
                                      <div className="other-reason-text">{reason.notes}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })()}

                          <div className="mistake-badges">
                            {isAcknowledged && (
                              <div className="acknowledged-badge">
                                ✅ Acknowledged
                                <button
                                  className="delete-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteClick('acknowledgment', mistake.human_label_id);
                                  }}
                                  title="Delete acknowledgment"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {disputeInfoByRunId.has(mistake.problem_run_id) && mistake.user_label !== "bad_task" && (() => {
                              const disputeInfo = disputeInfoByRunId.get(mistake.problem_run_id)!;
                              const { status, proposedScore } = disputeInfo;
                              const statusConfig = {
                                pending: { icon: '⏳', text: 'Pending', className: 'disputed-badge pending' },
                                accepted: { icon: '✅', text: 'Accepted', className: 'disputed-badge accepted' },
                                rejected: { icon: '❌', text: 'Rejected', className: 'disputed-badge rejected' }
                              };
                              const proposedScoreLabels: Record<string, string> = {
                                success: 'Success',
                                failed: 'Failed',
                                bad_task: 'Bad Task'
                              };
                              const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
                              const proposedLabel = proposedScoreLabels[proposedScore] || proposedScore;
                              const canDelete = status === 'pending';

                              return (
                                <div className={config.className}>
                                  {config.icon} Proposed: {proposedLabel}, Dispute {config.text}
                                  {canDelete && (
                                    <button
                                      className="delete-btn"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDeleteClick('dispute', mistake.problem_run_id);
                                      }}
                                      title="Delete dispute"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
              {incorrectLabelsTotal > 20 && (
                <div className="pagination">
                  <button
                    disabled={incorrectLabelsPage === 0}
                    onClick={() =>
                      loadIncorrectLabelsData(incorrectLabelsPage - 1)
                    }
                  >
                    Previous
                  </button>
                  <span>
                    Page {incorrectLabelsPage + 1} of{" "}
                    {Math.ceil(incorrectLabelsTotal / 20)}
                  </span>
                  <button
                    disabled={
                      (incorrectLabelsPage + 1) * 20 >= incorrectLabelsTotal
                    }
                    onClick={() =>
                      loadIncorrectLabelsData(incorrectLabelsPage + 1)
                    }
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === "my-disputes" && hasLabelPermission ? (
        <>
          {loadingDisputes ? (
            <div className="project-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="project-card dispute-card skeleton-card">
                  <Skeleton height={24} className="skeleton-title" style={{ marginBottom: 8 }} />
                  <div style={{ marginBottom: 12 }}>
                    <Skeleton height={20} width="80%" style={{ marginBottom: 8 }} />
                    <Skeleton height={16} width="60%" />
                  </div>
                  <Skeleton height={16} width="40%" style={{ marginTop: 12 }} />
                </div>
              ))}
            </div>
          ) : disputes.length === 0 ? (
            <div className="no-projects">
              You have not submitted any disputes yet.
            </div>
          ) : (
            <>
              <div className="project-grid">
                {disputes.map((dispute) => (
                  <a
                    key={dispute.id}
                    href={`/run/${dispute.problem_run_id}?mode=dispute&disputeId=${dispute.id}`}
                    className={`project-card dispute-card ${hoveredDeleteButton === dispute.id ? 'no-hover' : ''}`}
                    onClick={(e) => {
                      // Allow command+click (Mac) or ctrl+click (Windows) to open in new tab
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                        return;
                      }
                      e.preventDefault();
                      // Navigate to the run in dispute mode
                      window.history.pushState({}, "", `/run/${dispute.problem_run_id}?mode=dispute&disputeId=${dispute.id}`);
                      
                      // Dispatch a custom event with the navigation details
                      const navigationEvent = new CustomEvent('navigateToRun', {
                        detail: {
                          runId: dispute.problem_run_id,
                          isDisputeMode: true,
                          disputeId: dispute.id,
                          disputeData: dispute
                        }
                      });
                      window.dispatchEvent(navigationEvent);
                    }}
                    style={{ textDecoration: "none" }}
                  >
                    {dispute.status === 'pending' && (
                      <button
                        className="delete-dispute-btn"
                        onClick={(e) => {
                          e.preventDefault(); // Prevent link navigation
                          e.stopPropagation(); // Prevent card click
                          setDisputeToDelete(dispute);
                          setShowDeleteConfirmModal(true);
                        }}
                        onMouseEnter={() => setHoveredDeleteButton(dispute.id)}
                        onMouseLeave={() => setHoveredDeleteButton(null)}
                        title="Delete dispute"
                      >
                        ✕
                      </button>
                    )}
                    <h3>{dispute.problem_name}</h3>
                    <div className="dispute-details">
                      <div className="dispute-status">
                        <span className="status-label">Status:</span>
                        <span className={`status-value ${dispute.status}`}>
                          {dispute.status.charAt(0).toUpperCase() + dispute.status.slice(1)}
                        </span>
                      </div>
                      {dispute.reason && (
                        <div className="dispute-reason">
                          <span className="reason-label">Reason:</span>
                          <span className="reason-value">{dispute.reason}</span>
                        </div>
                      )}
                      <div className="dispute-note">
                        <span className="note-label">Your Note:</span>
                        <span className="note-value">{dispute.note}</span>
                      </div>
                      <div className="dispute-proposed">
                        <span className="proposed-label">Proposed Score:</span>
                        <span
                          className={`proposed-value ${dispute.proposed_score}`}
                        >
                          {dispute.proposed_score === "success"
                            ? "Success"
                            : dispute.proposed_score === "failed"
                              ? "Failed"
                              : "Bad Task"}
                        </span>
                      </div>
                      <div className="dispute-date">
                        Submitted on{" "}
                        {new Date(dispute.created_at + "Z").toLocaleString()}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
              {disputesTotal > 20 && (
                <div className="pagination">
                  <button
                    disabled={disputesPage === 0}
                    onClick={() => loadDisputesData(disputesPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {disputesPage + 1} of {Math.ceil(disputesTotal / 20)}
                  </span>
                  <button
                    disabled={(disputesPage + 1) * 20 >= disputesTotal}
                    onClick={() => loadDisputesData(disputesPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === "disputes" ? (
        <>
          <div className="disputes-header">
            <button 
              className="info-button"
              onClick={() => setShowDisputeInfoModal(true)}
              title="How to evaluate disputes"
            >
              ℹ️ How to evaluate disputes
            </button>
            <div className="sort-controls">
              <label>Sort by:</label>
              <select 
                value={disputesSortBy}
                onChange={(e) => setDisputesSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="problem_name">Problem Name</option>
                <option value="created_at">Date Created</option>
              </select>
              <select 
                value={disputesSortOrder}
                onChange={(e) => setDisputesSortOrder(e.target.value)}
                className="sort-select"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
          {loadingAllDisputes ? (
            <div className="project-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="project-card dispute-card skeleton-card">
                  <Skeleton height={24} className="skeleton-title" style={{ marginBottom: 8 }} />
                  <div style={{ marginBottom: 12 }}>
                    <Skeleton height={20} width="90%" style={{ marginBottom: 8 }} />
                    <Skeleton height={16} width="70%" />
                  </div>
                  <Skeleton height={40} style={{ marginBottom: 8 }} />
                  <Skeleton height={14} width="30%" />
                </div>
              ))}
            </div>
          ) : allDisputes.length === 0 ? (
            <div className="no-projects">
              No pending disputes to review.
            </div>
          ) : (
            <>
              <div className="project-grid">
                {allDisputes.map((dispute) => (
                  <a
                    key={dispute.id}
                    href={`/run/${dispute.problem_run_id}?mode=dispute&disputeId=${dispute.id}`}
                    className="project-card dispute-card"
                    onClick={(e) => {
                      // Allow command+click (Mac) or ctrl+click (Windows) to open in new tab
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                        return;
                      }
                      e.preventDefault();
                      // Navigate directly to the problem run in dispute mode
                      
                      // Update the URL
                      window.history.pushState({}, "", `/run/${dispute.problem_run_id}?mode=dispute&disputeId=${dispute.id}`);
                      
                      // Dispatch a custom event with the navigation details
                      const navigationEvent = new CustomEvent('navigateToRun', {
                        detail: {
                          runId: dispute.problem_run_id,
                          isDisputeMode: true,
                          disputeId: dispute.id,
                          disputeData: dispute
                        }
                      });
                      window.dispatchEvent(navigationEvent);
                    }}
                    style={{ textDecoration: "none" }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <h3 style={{ margin: 0 }}>{dispute.problem_name || "Loading..."}</h3>
                      {dispute.is_problem_creator && (
                        <span
                          title="This dispute was created by the problem creator"
                          style={{
                            backgroundColor: '#f0f9ff',
                            color: '#0369a1',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            border: '1px solid #bae6fd',
                          }}
                        >
                          Problem Creator
                        </span>
                      )}
                    </div>
                    <div className="dispute-details">
                      <div className="dispute-scores">
                        <div className="score-item">
                          <span className="score-label">Current Score:</span>
                          <span className={`score-value ${dispute.final_score === 1 ? 'success' : dispute.final_score === 0 ? 'failed' : ''}`}>
                            {dispute.final_score === 1 ? "Success" : dispute.final_score === 0 ? "Failed" : "Not scored"}
                          </span>
                        </div>
                        <div className="score-item">
                          <span className="score-label">Disputed Score:</span>
                          <span className={`score-value ${dispute.proposed_score}`}>
                            {dispute.proposed_score === "success"
                              ? "Success"
                              : dispute.proposed_score === "failed"
                                ? "Failed"
                                : "Bad Task"}
                          </span>
                        </div>
                      </div>
                      <div className="dispute-note">
                        <span className="note-label">Dispute Note:</span>
                        <span className="note-value">{dispute.note}</span>
                      </div>
                      <div className="dispute-date">
                        Submitted on{" "}
                        {new Date(dispute.created_at + "Z").toLocaleString()}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
              {allDisputesTotal > 20 && (
                <div className="pagination">
                  <button
                    disabled={allDisputesPage === 0}
                    onClick={() => loadAllDisputesData(allDisputesPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {allDisputesPage + 1} of {Math.ceil(allDisputesTotal / 20)}
                  </span>
                  <button
                    disabled={(allDisputesPage + 1) * 20 >= allDisputesTotal}
                    onClick={() => loadAllDisputesData(allDisputesPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === "resolved-disputes" ? (
        <>
          <div className="disputes-header">
            <div className="search-controls">
              <input
                type="text"
                placeholder="Search disputes..."
                value={resolvedDisputesSearchInput}
                onChange={(e) => setResolvedDisputesSearchInput(e.target.value)}
                className="search-input"
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  width: '300px'
                }}
              />
              {resolvedDisputesSearchInput && (
                <button
                  onClick={() => {
                    setResolvedDisputesSearchInput('');
                    setResolvedDisputesSearchQuery('');
                  }}
                  className="clear-search-btn"
                  style={{
                    marginLeft: '8px',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="sort-controls">
              <label>Sort by:</label>
              <select
                value={resolvedDisputesSortBy}
                onChange={(e) => setResolvedDisputesSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="problem_name">Problem Name</option>
                <option value="updated_at">Date Resolved</option>
                <option value="created_at">Date Created</option>
              </select>
              <select
                value={resolvedDisputesSortOrder}
                onChange={(e) => setResolvedDisputesSortOrder(e.target.value)}
                className="sort-select"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
          {loadingResolvedDisputes ? (
            <div className="project-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="project-card dispute-card">
                  <div style={{ marginBottom: 8 }}>
                    <Skeleton height={20} width="25%" style={{ marginBottom: 12 }} />
                  </div>
                  <Skeleton height={24} className="skeleton-title" style={{ marginBottom: 16 }} />
                  <div style={{ marginBottom: 16 }}>
                    <Skeleton height={20} width="80%" style={{ marginBottom: 8 }} />
                    <Skeleton height={20} width="80%" />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <Skeleton height={14} width="100%" style={{ marginBottom: 6 }} />
                    <Skeleton height={40} />
                  </div>
                  <Skeleton height={14} width="90%" />
                </div>
              ))}
            </div>
          ) : resolvedDisputes.length === 0 ? (
            <div className="no-projects">
              {resolvedDisputesSearchQuery
                ? `No resolved disputes found matching "${resolvedDisputesSearchQuery}".`
                : "No resolved disputes found."}
            </div>
          ) : (
            <>
              <div className="project-grid">
                {resolvedDisputes.map((dispute) => (
                  <a
                    key={dispute.id}
                    href={`/run/${dispute.problem_run_id}?mode=dispute&disputeId=${dispute.id}`}
                    className={`project-card dispute-card resolved-dispute ${dispute.status === 'accepted' ? 'accepted' : 'rejected'}`}
                    onClick={(e) => {
                      // Allow command+click (Mac) or ctrl+click (Windows) to open in new tab
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                        return;
                      }
                      e.preventDefault();
                      // Navigate to the run in dispute mode
                      window.history.pushState({}, "", `/run/${dispute.problem_run_id}?mode=dispute&disputeId=${dispute.id}`);
                      
                      // Dispatch a custom event with the navigation details
                      const navigationEvent = new CustomEvent('navigateToRun', {
                        detail: {
                          runId: dispute.problem_run_id,
                          isDisputeMode: true,
                          disputeId: dispute.id,
                          disputeData: dispute
                        }
                      });
                      window.dispatchEvent(navigationEvent);
                    }}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="dispute-status-badge">
                      {dispute.status === 'accepted' ? '✓ Accepted' : '✗ Rejected'}
                    </div>
                    <h3>{dispute.problem_name || "Loading..."}</h3>
                    
                    <div className="dispute-details">
                      {/* For rejected bad task disputes with no final_status, show different UI */}
                      {!dispute.final_status && dispute.status === 'rejected' && dispute.note?.startsWith('Marked as Bad Task from the Label Platform for reason:') ? (
                        <div className="dispute-scores-compact">
                          <div className="score-change">
                            <span className="score-label">Resolution:</span>
                            <span className="score-value" style={{ color: '#6b7280' }}>
                              Dispute rejected - no score changes applied
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="dispute-scores-compact">
                          <div className="score-change">
                            <span className="score-label">Original Score:</span>
                            <span className={`score-value ${dispute.final_score === 1 ? 'success' : 'failed'}`}>
                              {dispute.final_score === 1 ? "Success" : "Failed"}
                            </span>
                          </div>
                          <span className="score-arrow">→</span>
                          <div className="score-change">
                            <span className="score-label">Final Score:</span>
                            <span className={`score-value ${
                              dispute.final_status === 'success' ? 'success' :
                              dispute.final_status === 'failed' ? 'failed' :
                              dispute.final_status === 'bad_task' ? 'bad_task' :
                              // Fallback for when final_status is not available
                              dispute.status === 'accepted' ? dispute.proposed_score :
                              dispute.reason && dispute.reason.includes("bad task") ? 'bad_task' :
                              (dispute.final_score === 1 ? 'success' : 'failed')
                            }`}>
                              {dispute.final_status ? (
                                dispute.final_status === "success" ? "Success" :
                                dispute.final_status === "failed" ? "Failed" :
                                dispute.final_status === "bad_task" ? "Bad Task" :
                                "Not Set"
                              ) : (
                                // Fallback logic when final_status is not available
                                dispute.status === 'accepted' ? (
                                  dispute.proposed_score === "success" ? "Success" :
                                  dispute.proposed_score === "failed" ? "Failed" :
                                  "Bad Task"
                                ) : (
                                  dispute.reason && dispute.reason.toLowerCase().includes("bad task") ? "Bad Task" :
                                  dispute.final_score === 1 ? "Success" : "Failed"
                                )
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="dispute-content">
                        <div className="dispute-note-section">
                          <div className="note-label">Dispute Note:</div>
                          <div className="note-text">{dispute.note}</div>
                        </div>
                        
                        {dispute.reason && (
                          <div className="resolution-section">
                            <div className="resolution-label">Resolution Reason:</div>
                            <div className="resolution-text">{dispute.reason}</div>
                          </div>
                        )}
                      </div>
                      
                      <div className="dispute-timeline">
                        <span className="timeline-item">
                          Submitted on {new Date(dispute.created_at + "Z").toLocaleDateString('en-US', { 
                            month: 'numeric', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                        <span className="timeline-separator">•</span>
                        <span className="timeline-item">
                          Resolved by you on {new Date(dispute.updated_at + "Z").toLocaleDateString('en-US', { 
                            month: 'numeric', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
              {resolvedDisputesTotal > 20 && (
                <div className="pagination">
                  <button
                    disabled={resolvedDisputesPage === 0}
                    onClick={() => loadResolvedDisputesData(resolvedDisputesPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {resolvedDisputesPage + 1} of {Math.ceil(resolvedDisputesTotal / 20)}
                  </span>
                  <button
                    disabled={(resolvedDisputesPage + 1) * 20 >= resolvedDisputesTotal}
                    onClick={() => loadResolvedDisputesData(resolvedDisputesPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === "conflicting-disputes" ? (
        <>
          <div className="disputes-header">
            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
              These are problem runs where you accepted disputes with conflicting final statuses (e.g., one marked as success AND another as failed).
            </p>
          </div>
          {loadingConflictingDisputes ? (
            <div className="project-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="project-card dispute-card skeleton-card">
                  <Skeleton height={24} className="skeleton-title" style={{ marginBottom: 8 }} />
                  <Skeleton height={16} width="70%" style={{ marginBottom: 16 }} />
                  <div style={{ marginBottom: 12 }}>
                    <Skeleton height={20} width="80%" style={{ marginBottom: 8 }} />
                    <Skeleton height={20} width="80%" />
                  </div>
                </div>
              ))}
            </div>
          ) : conflictingDisputes.length === 0 ? (
            <div className="no-projects">
              No conflicting dispute resolutions found.
            </div>
          ) : (
            <>
              <div className="project-grid">
                {conflictingDisputes.map((run) => (
                  <div
                    key={run.problem_run_id}
                    className="project-card dispute-card conflicting-dispute"
                    style={{ borderLeft: '4px solid #ef4444' }}
                  >
                    <h3>{run.problem_name}</h3>
                    <div className="dispute-details">
                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>
                          Software: {run.problem_software}
                        </span>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ fontWeight: 500, color: '#dc2626' }}>
                          Conflicting Final Statuses: {run.distinct_final_statuses.join(', ')}
                        </span>
                      </div>

                      <div className="conflicting-disputes-list" style={{ marginTop: '12px' }}>
                        {run.disputes.map((dispute, idx) => (
                          <div
                            key={dispute.dispute_id}
                            style={{
                              padding: '10px',
                              backgroundColor: idx % 2 === 0 ? '#f9fafb' : '#fff',
                              borderRadius: '4px',
                              marginBottom: '8px',
                              border: '1px solid #e5e7eb'
                            }}
                          >
                            <div style={{ marginBottom: '4px' }}>
                              <span className={`score-value ${dispute.final_status}`} style={{ fontSize: '12px', fontWeight: 500 }}>
                                Final: {dispute.final_status === 'success' ? 'Success' :
                                       dispute.final_status === 'failed' ? 'Failed' :
                                       dispute.final_status === 'bad_task' ? 'Bad Task' : 'N/A'}
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                              Proposed: {dispute.proposed_score === 'success' ? 'Success' :
                                        dispute.proposed_score === 'failed' ? 'Failed' : 'Bad Task'}
                            </div>
                            <div style={{ fontSize: '13px', color: '#374151' }}>
                              Note: {dispute.note}
                            </div>
                            {dispute.created_at && (
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                Created: {new Date(dispute.created_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <a
                        href={`/run/${run.problem_run_id}?mode=dispute`}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                            return;
                          }
                          e.preventDefault();
                          window.history.pushState({}, "", `/run/${run.problem_run_id}?mode=dispute`);
                          const navigationEvent = new CustomEvent('navigateToRun', {
                            detail: {
                              runId: run.problem_run_id,
                              isDisputeMode: true
                            }
                          });
                          window.dispatchEvent(navigationEvent);
                        }}
                        style={{
                          display: 'inline-block',
                          marginTop: '12px',
                          padding: '8px 16px',
                          backgroundColor: '#3b82f6',
                          color: '#fff',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                      >
                        View Problem Run
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              {conflictingDisputesTotal > 20 && (
                <div className="pagination">
                  <button
                    disabled={conflictingDisputesPage === 0}
                    onClick={() => loadConflictingDisputesData(conflictingDisputesPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {conflictingDisputesPage + 1} of {Math.ceil(conflictingDisputesTotal / 20)}
                  </span>
                  <button
                    disabled={(conflictingDisputesPage + 1) * 20 >= conflictingDisputesTotal}
                    onClick={() => loadConflictingDisputesData(conflictingDisputesPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === "playground" && hasPlaygroundAccess ? (
        <PlaygroundContent userPermissions={userPermissions} />
      ) : null}
      
      <DisputeInfoModal 
        isOpen={showDisputeInfoModal}
        onClose={() => setShowDisputeInfoModal(false)}
      />
      
      {/* Delete Dispute Confirmation Modal */}
      {showDeleteConfirmModal && disputeToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowDeleteConfirmModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
              Delete Dispute
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to delete this dispute?
            </p>
            <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Problem:</strong> {disputeToDelete.problem_name}<br />
              <strong>Note:</strong> {disputeToDelete.note}
            </p>
            <p style={{ color: '#dc2626', fontWeight: 500, margin: '0 0 20px 0', fontSize: '14px' }}>
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setDisputeToDelete(null);
                }}
                disabled={deletingDisputeId !== null}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onClick={handleDeleteDispute}
                disabled={deletingDisputeId !== null}
              >
                {deletingDisputeId ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unified Delete Modal for Mistakes */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
              Confirm Delete
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to delete this {deleteType === 'dispute' ? 'dispute' : 'mistake acknowledgment'}?
            </p>
            <p style={{ color: '#dc2626', fontWeight: 500, margin: '0 0 20px 0', fontSize: '14px' }}>
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList;
